import { getMainLanguage, SupportedLocale, supportedLocales } from "@/i18n";
import { cn } from "@/lib/utils";
import { SelectProps } from "@radix-ui/react-select";
import { HTMLProps, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export const MultilingualSelect = ({
  values,
  ...props
}: SelectProps & { values?: SupportedLocale[] }) => {
  const { t } = useTranslation();
  return (
    <Select {...props}>
      <SelectTrigger className="w-fit font-semibold">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(values ?? supportedLocales).map((locale) => (
          <SelectItem key={locale} value={locale}>
            {t(`i18n.languages.${locale}.minified`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export type MultilingualFieldProps = (
  | {
      fixed: SupportedLocale;
      defaultLang?: never;
      excludeLang?: never;
    }
  | {
      defaultLang?: SupportedLocale;
      excludeLang?: SupportedLocale;
      fixed?: never;
    }
) & {
  corner?: (selectedLocale: SupportedLocale) => ReactNode;
  render: (selectedLocale: SupportedLocale) => ReactNode;
} & HTMLProps<HTMLDivElement>;

export const MultilingualField = ({
  render,
  fixed,
  defaultLang,
  excludeLang,
  className,
  corner,
  ...props
}: MultilingualFieldProps) => {
  const { t } = useTranslation();

  const availableLocales = (
    excludeLang
      ? supportedLocales.filter((l) => l != excludeLang)
      : supportedLocales
  ) as SupportedLocale[];

  const [selectedLocale, setSelectedLocale] = useState(
    fixed ?? defaultLang ?? availableLocales[0],
  );

  return (
    <div
      className={cn(
        "flex flex-col rounded-2xl border border-gray-300",
        className,
      )}
      {...props}
    >
      <div className="rounded-t-2xl h-14 bg-gray-50 border-b border-gray-300 py-2 px-4 flex items-center justify-between">
        {fixed ? (
          <div className="flex items-center">
            <span className="text-sm font-semibold ml-2">
              {t(`i18n.languages.${fixed}.minified`)}
            </span>
          </div>
        ) : (
          <div className="flex items-center">
            <MultilingualSelect
              values={availableLocales}
              value={selectedLocale}
              onValueChange={(value) =>
                setSelectedLocale(value as SupportedLocale)
              }
            />
          </div>
        )}
        {corner && <div key={selectedLocale}>{corner(selectedLocale)}</div>}
      </div>
      <div key={selectedLocale} className="p-4">
        {render(selectedLocale)}
      </div>
    </div>
  );
};

export const DoubleMultilingualField = ({
  className,
  ...props
}: Omit<MultilingualFieldProps, "fixed" | "defaultLang" | "excludeLang">) => {
  return (
    <>
      <MultilingualField
        className={cn(className, "flex-1")}
        {...props}
        fixed={getMainLanguage()}
      />
      <MultilingualField
        className={cn(className, "flex-1")}
        {...props}
        excludeLang={getMainLanguage()}
      />
    </>
  );
};
